import classnames from 'classnames'
import AgentRequestedOptions from 'components/AgentRequestedOptions'
import Message from 'components/Message'
import RecipientAvatar from 'components/RecipientAvatar'
import useAgentName from 'hooks/useAgentName'
import useThemeColors from 'hooks/useThemeColors'
import DateTimeManager from 'modules/dateTime'
import { hasMessageAttribute, isEndUserMessage, isMeetingScheduleMessage } from 'modules/message/utils'
import React, { Fragment, useLayoutEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Message as MessageModel, MessageSenderType } from 'types/message'

import './style.css'

interface MessageGroupHeaderProps {
  senderType: string,
  senderId: number
}

interface MessageGroupProps {
  messages: MessageModel[],
  isLastGroup?: boolean,
  showTimestamp?: boolean,
  resolveMessageHeight?: () => void,
  role?: string
}

const getAvatarPositionClassName = (message) => {
  const hasScheduleMeeting = isMeetingScheduleMessage(message)
  const { show_email_capture_card = false } = message.attributes
  const avatarPosition = show_email_capture_card || hasScheduleMeeting ? 'top' : 'bottom'
  return `message-group-avatar-${avatarPosition}`
}

const formatTimestamp = (time, t) => {

  if (time > 0) {
    if (DateTimeManager.isTimeSame(time, 'date')) {
      return `${t('dt.today')} ${DateTimeManager.unixFormatDateTime(time, t('dt.format.time.short'))}`
    }

    return DateTimeManager.unixFormatDateTime(time, t('dt.format.datetime.short'))
  }
  return ''
}

const MessageGroupHeader = ({ senderType, senderId }: MessageGroupHeaderProps) => {
  const agentName = useAgentName(senderId)
  const { messageAuthorTheme } = useThemeColors()

  if (senderType !== MessageSenderType.AGENT) {
    return null
  }

  return (
    <li className="drift-widget-message-sender">
      <span className="drift-widget-message--meta-author" style={messageAuthorTheme}>{agentName}</span>
    </li>
  )
}

const MessageGroup = ({
  messages = [],
  isLastGroup = false,
  showTimestamp = false,
  role = 'cell',
  resolveMessageHeight = () => {}
}: MessageGroupProps) => {
  const { t } = useTranslation()

  const { messageTimestampTheme } = useThemeColors()

  useLayoutEffect(() => {
    resolveMessageHeight()
  }, [resolveMessageHeight, isLastGroup, showTimestamp])

  const [firstMessage] = messages

  const senderType = isEndUserMessage(firstMessage)
  ? MessageSenderType.END_USER
  : MessageSenderType.AGENT

  const senderId = firstMessage.authorId
  const lastMessageInGroup = messages[messages.length - 1]
  const shouldRenderButtons = (
    isLastGroup &&
    hasMessageAttribute(lastMessageInGroup, 'buttons')
  )

  return (
    <div
      role={role}
      className={classnames("drift-widget-message-group-wrapper", {
        "drift-widget-message-group-wrapper--last": isLastGroup
      })}
    >
      {showTimestamp &&
        <p className="drift-widget-message-group-timestamp" style={messageTimestampTheme}>
          {formatTimestamp(messages[0].createdAt, t)}
        </p>
      }
      <ul className={`drift-widget-message-group drift-widget-message-group-type--${senderType}`}>
        <MessageGroupHeader
          senderType={senderType}
          senderId={senderId}
        />

        {messages.map((message, index) => {
          const isLastMessage = index === (messages.length - 1)
          const nextMessage = isLastMessage ? null : messages[index + 1]

          return (
            <Fragment key={message.attributes.generatedAt || message.id || message.attributes.widgetGuid}>
              {(isLastMessage && senderType === MessageSenderType.AGENT) &&
                <li
                  className={classnames(
                    'drift-widget-message-group-avatar',
                    getAvatarPositionClassName(message)
                  )}
                >
                  <RecipientAvatar id={senderId} size="SMALL"/>
                </li>
              }
              <Message
                resolveMessageHeight={resolveMessageHeight}
                message={message}
                nextMessage={nextMessage}
                isLastMessage={isLastMessage}
                isLastGroup={isLastGroup}
              />
            </Fragment>
          )
        })}
      </ul>

      {shouldRenderButtons &&
        <AgentRequestedOptions
          message={lastMessageInGroup}
        />
      }
    </div>
  )
}

export default MessageGroup
